import { Component } from '@theme/component'

const EDGE_TOLERANCE = 1

class UseSlider extends Component {
  requiredRefs = ['scroller']

  currentIndex = 0

  _cachedDimensions = {
    clientWidth: 0,
    scrollWidth: 0
  }

  _cardScrollPositions = []
  _contentObserver = null
  _resizeObserver = null
  _resizeRAF = null
  _scrollRAF = null

  connectedCallback() {
    super.connectedCallback()

    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    scroller.addEventListener('scroll', this._handleScroll, { passive: true })
    window.addEventListener('resize', this._handleResize, { passive: true })

    this._observeSliderSizing()
    this._refreshSliderState()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    const { scroller } = this.refs
    if (scroller) {
      scroller.removeEventListener('scroll', this._handleScroll)
    }
    window.removeEventListener('resize', this._handleResize)
    this._disconnectSliderSizing()
    this._clearPendingWork()
  }

  next(event) {
    event?.preventDefault?.()
    this._selectByOffset(1)
  }

  prev(event) {
    event?.preventDefault?.()
    this._selectByOffset(-1)
  }

  select(index, event) {
    event?.preventDefault?.()
    this._cacheDimensions()
    this._scrollToIndex(index)
  }

  _selectByOffset(offset) {
    this._cacheDimensions()

    const currentIndex = this._getCurrentIndex()
    if (currentIndex === null) {
      return
    }

    this._scrollToIndex(currentIndex + offset)
  }

  _scrollToIndex(index) {
    const positions = this._cardScrollPositions
    if (!positions.length) {
      return
    }

    const nextIndex = Math.max(0, Math.min(index, positions.length - 1))
    this._scrollTo(positions[nextIndex])
    this._setCurrentIndex(nextIndex)
  }

  _observeSliderSizing() {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    this._resizeObserver = new ResizeObserver(this._queueSliderStateUpdate)
    this._resizeObserver.observe(scroller)

    const content = this._getScrollerContent()
    if (content) {
      this._resizeObserver.observe(content)
    }

    this._contentObserver = new MutationObserver(this._queueSliderStateUpdate)
    this._contentObserver.observe(scroller, { childList: true, subtree: true })
  }

  _disconnectSliderSizing() {
    this._contentObserver?.disconnect()
    this._contentObserver = null
    this._resizeObserver?.disconnect()
    this._resizeObserver = null
  }

  _queueSliderStateUpdate = () => {
    if (this._resizeRAF) {
      cancelAnimationFrame(this._resizeRAF)
    }

    this._resizeRAF = requestAnimationFrame(() => {
      this._resizeRAF = null
      this._refreshSliderState()
    })
  }

  _refreshSliderState() {
    this._cacheDimensions()
    this._syncScrollPosition()
    this._syncScrollState()
  }

  _cacheDimensions() {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    const { scrollWidth, clientWidth } = scroller

    this._cachedDimensions = { clientWidth, scrollWidth }
    this._cardScrollPositions = this._measureCardScrollPositions()
  }

  _measureCardScrollPositions() {
    const { scroller } = this.refs
    const cards = this._getCards()
    if (!cards.length) {
      return []
    }

    const scrollerLeft = scroller.getBoundingClientRect().left
    const scrollLeft = scroller.scrollLeft
    const scrollPaddingStart = this._getScrollPaddingStart()

    return cards.map((card) => {
      const cardOffset = card.getBoundingClientRect().left - scrollerLeft
      return scrollLeft + cardOffset - scrollPaddingStart
    })
  }

  _getScrollPaddingStart() {
    const { scroller } = this.refs
    const scrollPadding = Number.parseFloat(
      getComputedStyle(scroller).scrollPaddingInlineStart
    )
    return Number.isNaN(scrollPadding) ? 0 : scrollPadding
  }

  _getMaxScroll() {
    const { scrollWidth, clientWidth } = this._cachedDimensions
    return Math.max(0, scrollWidth - clientWidth)
  }

  _clampScrollLeft(position) {
    return Math.max(0, Math.min(position, this._getMaxScroll()))
  }

  _getCards() {
    const { scroller } = this.refs
    if (!scroller) {
      return []
    }

    const selector = this.getAttribute('selector') || '.card'
    return Array.from(scroller.querySelectorAll(selector))
  }

  _getDots() {
    if (!this.id) {
      return []
    }
    return Array.from(
      document.querySelectorAll(`.dots__dot[aria-controls="${this.id}"]`)
    )
  }

  _getCurrentIndex() {
    const { scroller } = this.refs
    const positions = this._cardScrollPositions
    if (!(scroller && positions.length)) {
      return null
    }

    const scrollLeft = scroller.scrollLeft
    let closestIndex = 0
    let minDistance = Number.POSITIVE_INFINITY

    for (const [index, position] of positions.entries()) {
      const distance = Math.abs(position - scrollLeft)
      if (distance >= minDistance) {
        continue
      }
      minDistance = distance
      closestIndex = index
    }

    return closestIndex
  }

  _getScrollerContent() {
    const { scroller } = this.refs
    const content = scroller?.firstElementChild
    return content instanceof HTMLElement ? content : null
  }

  _scrollTo(position) {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    scroller.scrollTo({
      behavior: 'smooth',
      left: this._clampScrollLeft(position)
    })
  }

  _handleScroll = () => {
    if (this._scrollRAF) {
      return
    }

    this._scrollRAF = requestAnimationFrame(() => {
      this._scrollRAF = null
      this._syncScrollState()
    })
  }

  _handleResize = () => {
    this._queueSliderStateUpdate()
  }

  _syncScrollPosition() {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    const nextScrollLeft = this._clampScrollLeft(scroller.scrollLeft)
    if (nextScrollLeft === scroller.scrollLeft) {
      return
    }

    scroller.scrollLeft = nextScrollLeft
  }

  _syncScrollState() {
    const { scroller } = this.refs
    if (!scroller) {
      return
    }

    this._setCurrentIndex(this._getCurrentIndex())

    const sliderState = this._getSliderState()
    this._dispatchSliderState(sliderState)
    this._syncEdgeAttributes(sliderState)
  }

  _getSliderState() {
    const { scroller } = this.refs
    const scrollLeft = scroller.scrollLeft
    const { scrollWidth, clientWidth } = this._cachedDimensions

    return {
      atEnd: scrollLeft + clientWidth >= scrollWidth - EDGE_TOLERANCE,
      atStart: scrollLeft <= EDGE_TOLERANCE
    }
  }

  _dispatchSliderState(sliderState) {
    this.dispatchEvent(
      new CustomEvent('slider-state-change', {
        bubbles: true,
        detail: sliderState
      })
    )
  }

  _syncEdgeAttributes({ atEnd, atStart }) {
    this.toggleAttribute('at-start', atStart)
    this.toggleAttribute('at-end', atEnd)
  }

  _setCurrentIndex(index) {
    if (index === null || Number.isNaN(index)) {
      return
    }
    if (this.currentIndex === index) {
      return
    }
    this.currentIndex = index
    this._updateDots()
  }

  _updateDots() {
    const dots = this._getDots()
    if (!dots.length) {
      return
    }
    for (const dot of dots) {
      const dotIndex = Number.parseInt(dot.dataset.index, 10)
      if (Number.isNaN(dotIndex)) {
        continue
      }
      dot.setAttribute(
        'aria-current',
        dotIndex === this.currentIndex ? 'true' : 'false'
      )
    }
  }

  _clearPendingWork() {
    if (this._scrollRAF) {
      cancelAnimationFrame(this._scrollRAF)
      this._scrollRAF = null
    }
    if (this._resizeRAF) {
      cancelAnimationFrame(this._resizeRAF)
      this._resizeRAF = null
    }
  }
}

if (!customElements.get('use-slider')) {
  customElements.define('use-slider', UseSlider)
}

export { UseSlider }
